import { useEffect, useRef, useState } from 'react';
import { calculateArea, calculateLineDistance, groupByMapLabel } from '@jumbo/utils/mapHelpers';
import { useToast } from 'context/tostifyContext';

const defaultCenter = { lat: 0, lng: 0 };
export const MAP_ITEM_OPTIONS = {
  pin: [
    { label: 'Sample Point', color: 'red' },
    { label: 'Travel Via', color: 'blue' },
    { label: 'Entrance', color: 'blue' },
    { label: 'Timber', color: 'green' },
    { label: 'Turntable', color: 'orange' },
    { label: 'ESB Pylons', color: 'yellow' },
    { label: 'Fueling Area', color: 'grey' },
    { label: 'Service Area', color: 'blue' },
    { label: 'Lowloader', color: 'purple' },
  ],
  line: [
    { label: 'Stacking Area Line', color: 'orange' },
    { label: 'Forest Road', color: '#653700' },
    { label: 'Public Road', color: 'black' },
    { label: 'Power Lines', color: 'yellow' },
    { label: 'Safe Cross Point', color: 'white' },
    { label: 'Main Extraction Line', color: 'red' },
    { label: 'Extraction Direction', color: 'pink' },
    { label: 'Drain', color: '#ADD8E6' },
    { label: 'Stream', color: '#4682B4' },
    { label: 'River', color: '#00008B' },
  ],
  polygon: [
    { label: 'Boundary', color: 'white' },
    { label: 'Stacking Area', color: 'blue' },
    { label: 'Sub Compartments', color: 'purple' },
    { label: 'Wet Area', color: 'grey' },
    { label: 'Bog', color: 'grey' },
    { label: 'Protected Area', color: 'grey' },
    { label: 'Clearfell Area', color: 'blue' },
    { label: 'Windblow', color: 'orange' },
  ],
};

export const useMap = ({ mapData, sectionData, open, isMapLoaded }) => {
  const { showToast } = useToast();
  const [center, setCenter] = useState(defaultCenter);
  const [zoom, setZoom] = useState(8);
  const [mode, setMode] = useState(null);

  const [pins, setPins] = useState([]);
  const [polygons, setPolygons] = useState([]);
  const [lines, setLines] = useState([]);

  const [currentPin, setCurrentPin] = useState(null);
  const [currentPolygon, setCurrentPolygon] = useState(null);
  const [currentPolygonData, setCurrentPolygonData] = useState(null);
  const [currentLine, setCurrentLine] = useState([]);
  const [currentLineData, setCurrentLineData] = useState(null);

  const [showPin, setShowPin] = useState(false);
  const [showPolygonDialog, setShowPolygonDialog] = useState(false);
  const [showLineDialog, setShowLineDialog] = useState(false);
  const [selected, setSelected] = useState(null);

  const [map, setMap] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  const [userLocation, setUserLocation] = useState(null);

  const mapRef = useRef(null);

  useEffect(() => {
    const pinData = mapData.filter(item => item.mapType === 'pin');
    setPins(pinData);

    const linesGrouped = groupByMapLabel(mapData, 'line');
    setLines(linesGrouped);

    const polygonsGrouped = groupByMapLabel(mapData, 'polygon');
    setPolygons(Object.keys(polygonsGrouped).length ? calculateArea(polygonsGrouped) : []);
  }, [mapData]);

  useEffect(() => {
    if (pins.length > 0) {
      setCenter({ lat: pins[0].latitude, lng: pins[0].longitude });
      setZoom(16);
    } else if (Object.values(polygons)?.length > 0) {
      const firstPolyCoord = Object.values(polygons)[0]?.coords[0];
      setCenter({ lat: firstPolyCoord.lat, lng: firstPolyCoord.lng });
      setZoom(15);
    } else if (open && isMapLoaded) {
      if (navigator.geolocation) {
        if (sectionData?.address1 || sectionData?.address2) {
          geocodeLocation(`${sectionData.address1 || ''}${sectionData.address2 || ''}`);
        } else {
          getCurrentUserLocation();
        }
      }
    }
  }, [open, isMapLoaded]);

  const handleMapLoad = mapInstance => {
    mapRef.current = mapInstance;
    setMap(mapInstance);
  };

  const handleZoomChanged = () => {
    if (mapRef.current) {
      setZoom(mapRef.current.getZoom());
    }
  };

  const getCurrentUserLocation = () => {
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setCenter({ lat: coords.latitude, lng: coords.longitude });
        setZoom(14);
      },
      error => {
        console.warn('Geolocation error:', error);
        geocodeLocation('Ireland');
      },
    );
  };

  const geocodeLocation = (location = 'Pune') => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: location }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const loc = results[0].geometry.location;
        setCenter({ lat: loc.lat(), lng: loc.lng() });
      }
    });
  };

  const generateId = () => `${Date.now()}-${Math.floor(Math.random() * 1000)}`;

  function isPointInsidePolygon(point, polygon) {
    let x = point.latitude,
      y = point.longitude;
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      let xi = polygon[i].lat,
        yi = polygon[i].lng;
      let xj = polygon[j].lat,
        yj = polygon[j].lng;

      let intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
      if (intersect) {
        inside = !inside;
      }
    }
    return inside;
  }

  const isPointNear = (p1, p2, threshold = 0.0001) =>
    Math.sqrt(Math.pow(p1.lat - p2.lat, 2) + Math.pow(p1.lng - p2.lng, 2)) < threshold;

  const handleMapPress = e => {
    console.log('E =====', e);
    const tappedPoint = e.latLng;
    if (mode === 'Pin') handleMapPin(tappedPoint);
    else if (mode === 'Polygon') handlePolygon(tappedPoint);
    else if (mode === 'Line') handleLine(tappedPoint);
  };

  const handleMapPin = tappedPoint => {
    const newPin = {
      id: generateId(),
      latitude: tappedPoint.lat(),
      longitude: tappedPoint.lng(),
      hexColor: 'black',
      mapLabel: '',
    };
    setPins(prev => [...prev, newPin]);
    setCurrentPin(newPin);
    setShowPin(true);
  };

  // const handlePolygon = tappedPoint => {
  //   console.log('TAPPED POINT ====', tappedPoint);
  //   const latLng = { lat: tappedPoint.lat(), lng: tappedPoint.lng() };

  //   if (!currentPolygon || !Array.isArray(currentPolygon.coords)) {
  //     setCurrentPolygon({ id: generateId(), mapLabel: '', hexColor: 'black', coords: [latLng], area: '' });
  //     return;
  //   }

  //   const coords = currentPolygon.coords;
  //   const idx = coords.findIndex((pt, i) => isPointNear(latLng, pt, i === 0 ? 0.0002 : 0.0001));

  //   if (idx !== -1) {
  //     if (idx === 0 && coords.length >= 3) {
  //       setPolygons(prev => [...prev, currentPolygon]);
  //       setCurrentPolygonData(currentPolygon);
  //       setCurrentPolygon(null);
  //       setShowPolygonDialog(true);
  //     } else {
  //       const updated = [...coords];
  //       updated.splice(idx, 1);
  //       setCurrentPolygon({ ...currentPolygon, coords: updated });
  //     }
  //   } else {
  //     setCurrentPolygon({ ...currentPolygon, coords: [...coords, latLng] });
  //   }
  // };

  const handlePolygon = tappedPoint => {
    const latLng = { lat: tappedPoint.lat(), lng: tappedPoint.lng() };

    if (!currentPolygon || !Array.isArray(currentPolygon.coords)) {
      setCurrentPolygon({
        id: generateId(),
        mapLabel: '',
        hexColor: 'black',
        coords: [latLng],
        area: '',
      });
      return;
    }

    // ONLY add point on map click
    setCurrentPolygon(prev => ({
      ...prev,
      coords: [...prev.coords, latLng],
    }));
  };

  const handleLine = tappedPoint => {
    const latLng = { lat: tappedPoint.lat(), lng: tappedPoint.lng() };

    setCurrentLine(prev => [...prev, latLng]);
  };

  // RESET
  const resetMap = () => {
    setCenter(defaultCenter);
    setZoom(8);
    setMode(null);

    setPins([]);
    setPolygons([]);
    setLines([]);

    setCurrentPin(null);
    setCurrentPolygon(null);
    setCurrentPolygonData(null);
    setCurrentLine([]);
    setCurrentLineData(null);

    setShowPin(false);
    setShowPolygonDialog(false);
    setShowLineDialog(false);
    setSelected(null);
  };

  // Handle Search
  const handleSearch = async () => {
    if (!searchText || !map) return;

    const text = searchText.trim();

    // 1️⃣ Check for lat,lng
    const latLngMatch = text.match(/^(-?\d+(\.\d+)?),\s*(-?\d+(\.\d+)?)$/);

    if (latLngMatch) {
      const lat = parseFloat(latLngMatch[1]);
      const lng = parseFloat(latLngMatch[3]);

      goToLocation(lat, lng);
      return;
    }

    // 2️⃣ Check for Google Maps link
    const urlMatch = text.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/) || text.match(/q=(-?\d+\.\d+),(-?\d+\.\d+)/);

    if (urlMatch) {
      const lat = parseFloat(urlMatch[1]);
      const lng = parseFloat(urlMatch[2]);

      goToLocation(lat, lng);
      return;
    }

    // 3️⃣ Otherwise → City / address search
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ address: text }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        goToLocation(location.lat(), location.lng());
      } else {
        alert('Location not found');
      }
    });
  };

  const goToLocation = (lat, lng) => {
    const position = { lat, lng };

    map.panTo(position);
    map.setZoom(16);
  };

  const goToUserLocation = () => {
    if (!navigator.geolocation) {
      showToast('Geolocation is not supported by this browser', 'error');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      position => {
        const userLoc = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };

        setUserLocation(userLoc);
        map.panTo(userLoc);
        map.setZoom(16);
      },
      error => {
        showToast('Unable to get your location', 'error');
        console.error(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
      },
    );
  };

  return {
    mapState: {
      center,
      zoom,
      pins,
      polygons,
      lines,
      currentLine,
      currentPolygon,
      selected,
      mode,
      currentPin,
      currentPolygonData,
      currentLineData,
      showPin,
      showPolygonDialog,
      showLineDialog,
      isPointInsidePolygon,
      map,
      searchText,
      searchOpen,
      userLocation,
    },
    mapActions: {
      setCenter,
      setZoom,
      setMode,
      setSelected,
      setCurrentPin,
      setCurrentPolygonData,
      setCurrentLineData,
      setShowPin,
      setShowPolygonDialog,
      setShowLineDialog,
      setLines,
      setPolygons,
      setPins,
      handleMapPress,
      setCurrentPolygon,
      setCurrentLine,
      calculateLineDistance,
      generateId,
      setCurrentPolygon,
      setPolygons,
      resetMap,
      setMap,
      setSearchText,
      handleSearch,
      setSearchOpen,
      goToUserLocation,
      handleMapLoad,
      handleZoomChanged,
    },
  };
};
